import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { VideoPlayer } from '@/components/common/VideoPlayer'
import { useFilm } from '../hooks/useFilm'
import { useAdjacentFilms } from '../hooks/useAdjacentFilms'
import { withErrorCode } from '@/lib/errors'

export function FilmDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { film, loading, error } = useFilm(slug)
  const { prev, next } = useAdjacentFilms(film)

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-16">
        <Skeleton className="mb-8 aspect-video w-full rounded-lg" />
        <Skeleton className="mb-3 h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-3/4" />
      </div>
    )
  }

  if (error || !film) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-destructive">
          {error ?? withErrorCode('Film not found.', 'seasons/film-not-found')}
        </p>
        <Link to="/seasons" className="mt-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to seasons
        </Link>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-16">
      <Link
        to="/seasons"
        className="mb-8 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> All seasons
      </Link>

      <div className="mb-8 flex items-stretch gap-2">
        <div className="flex w-10 flex-shrink-0 items-center justify-center">
          {prev ? (
            <Link
              to={`/films/${prev.slug}`}
              title={`S${prev.seasonNumber}E${prev.episodeNumber} – ${prev.title}`}
              className="flex h-full w-full items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
          ) : (
            <div className="h-full w-full" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <VideoPlayer videoUrl={film.videoUrl} youtubeId={film.youtubeId} title={film.title} />
        </div>

        <div className="flex w-10 flex-shrink-0 items-center justify-center">
          {next ? (
            <Link
              to={`/films/${next.slug}`}
              title={`S${next.seasonNumber}E${next.episodeNumber} – ${next.title}`}
              className="flex h-full w-full items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
            >
              <ChevronRight className="h-5 w-5" />
            </Link>
          ) : (
            <div className="h-full w-full" />
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">{film.title}</h1>
            {(film.type ?? 'episode') === 'episode' && (
              <div className="flex gap-2">
                <Badge variant="outline">Season {film.seasonNumber}</Badge>
                <Badge variant="outline">Episode {film.episodeNumber}</Badge>
              </div>
            )}
          </div>
        </div>

        <p className="leading-relaxed text-muted-foreground max-w-prose">{film.description}</p>

        {film.choreographers.length > 0 && (
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Choreographed by
            </h2>
            <div className="flex flex-wrap gap-4">
              {film.choreographers.map((c) => (
                <Link
                  key={c.slug}
                  to={`/company/${c.slug}`}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={c.photoUrl} alt={c.name} />
                    <AvatarFallback>
                      {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {film.cast.length > 0 && (
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Cast
            </h2>
            <div className="flex flex-wrap gap-4">
              {film.cast.map((c) => (
                <Link
                  key={c.slug}
                  to={`/company/${c.slug}`}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={c.photoUrl} alt={c.name} />
                    <AvatarFallback>
                      {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {film.crew.length > 0 && (
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              Crew
            </h2>
            <div className="flex flex-wrap gap-4">
              {film.crew.map((c) => (
                <Link
                  key={c.slug}
                  to={`/company/${c.slug}`}
                  className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-accent"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={c.photoUrl} alt={c.name} />
                    <AvatarFallback>
                      {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{c.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
